import Layout from '@/components/Layout';
import { Link } from 'react-router-dom';
import { Phone, MapPin, UserCheck, Heart, Activity, Home } from 'lucide-react';

export default function HomeVisitsPage() {
  const whoIsItFor = [
    "Struggles to walk safely",
    "Recently had a stroke",
    "Has Parkinson's disease",
    "Is recovering after surgery",
    "Has severe back or joint pain",
    "Is elderly and becoming weaker",
    "Is at risk of falling",
    "Cannot drive due to pain or dizziness"
  ];

  const conditionsNeurological = [
    "Stroke recovery",
    "Parkinson's disease",
    "Balance and falls problems",
    "Weakness after hospital stay",
    "Walking retraining"
  ];

  const conditionsPain = [
    "Sciatica",
    "Arthritis pain",
    "Muscle injuries"
  ];

  const conditionsPostSurgery = [
    "Fractures",
    "Immobilisation weakness",
    "Hospital discharge rehabilitation"
  ];

  const visitIncludes = [
    "Safety assessment of movement at home",
    "Hands-on physiotherapy treatment",
    "Walking and balance training",
    "Strength exercises using home environment",
    "Education for patient and family",
    "Personal recovery plan"
  ];

  const areas = [
    "Fourways", "Sandton", "Randburg", "Midrand", "Bryanston",
    "Rivonia", "Sunninghill", "Rooderpoort", "Krugersdorp"
  ];

  return (
    <Layout hideFooter={true}>
      {/* Hero Section */}
      <section className="py-8 bg-[#5ce1e6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Home Physiotherapy Visits in Johannesburg
          </h1>
          <p className="text-sm text-slate-800 max-w-4xl mb-2">
            We provide mobile physiotherapy treatment in the comfort and safety of your home for patients who cannot easily travel to the practice near Cedar Square Shopping Centre.
          </p>
          <p className="text-sm text-slate-700 max-w-4xl">
            Home treatment allows recovery to happen in a familiar environment, which is especially helpful for elderly patients, neurological conditions and post-surgery rehabilitation.
          </p>
        </div>
      </section>

      {/* Separator */}
      <div className="border-t-4 border-slate-400 w-full"></div>

      {/* Who Is It For */}
      <section className="py-6 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-4">
            <UserCheck className="w-5 h-5 text-[#0e7490]" />
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Who This Service Is For
            </h2>
          </div>
          <p className="text-sm text-slate-600 mb-3">Home physiotherapy is recommended if you or a family member:</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {whoIsItFor.map((item, index) => (
              <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                <div className="w-1.5 h-1.5 rounded-full bg-[#5ce1e6] flex-shrink-0" />
                <span className="text-xs text-slate-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Conditions We Treat */}
      <section className="py-6 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-4">
            <Heart className="w-5 h-5 text-[#0e7490]" />
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Conditions We Treat at Home
            </h2>
          </div>
          <p className="text-xs text-slate-600 mb-3">Additional to treatments listed on Treatments page</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Neurological */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-[#0e7490] mb-2">Neurological Rehabilitation</h3>
              <ul className="space-y-1">
                {conditionsNeurological.map((condition, index) => (
                  <li key={index} className="flex items-center gap-2 text-xs text-slate-600">
                    <div className="w-1 h-1 rounded-full bg-[#5ce1e6]" />
                    {condition}
                  </li>
                ))}
              </ul>
            </div>

            {/* Pain & Orthopaedic */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-[#0e7490] mb-2">Pain & Orthopaedic Conditions</h3>
              <ul className="space-y-1">
                {conditionsPain.map((condition, index) => (
                  <li key={index} className="flex items-center gap-2 text-xs text-slate-600">
                    <div className="w-1 h-1 rounded-full bg-[#5ce1e6]" />
                    {condition}
                  </li>
                ))}
              </ul>
            </div>

            {/* Post-Surgery */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-[#0e7490] mb-2">Post-Surgery Recovery</h3>
              <ul className="space-y-1">
                {conditionsPostSurgery.map((condition, index) => (
                  <li key={index} className="flex items-center gap-2 text-xs text-slate-600">
                    <div className="w-1 h-1 rounded-full bg-[#5ce1e6]" />
                    {condition}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What Happens During Visit */}
      <section className="py-6 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-5 h-5 text-[#0e7490]" />
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              What Happens During a Home Visit
            </h2>
          </div>
          <p className="text-sm text-slate-600 mb-3">Each visit includes:</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
            {visitIncludes.map((item, index) => (
              <div key={index} className="flex items-start gap-2 p-2 bg-[#f0fdfd] rounded-lg">
                <div className="w-5 h-5 rounded-full bg-[#5ce1e6] flex items-center justify-center flex-shrink-0">
                  <span className="text-slate-900 text-xs font-bold">{index + 1}</span>
                </div>
                <span className="text-xs text-slate-700">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-700 bg-slate-50 p-3 rounded-lg">
            <strong>Demonstration to caregivers how to assist safely without causing injury.</strong>
          </p>
        </div>
      </section>

      {/* Areas We Cover */}
      <section className="py-6 bg-[#5ce1e6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-5 h-5 text-slate-900" />
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Areas We Cover
            </h2>
          </div>
          <p className="text-sm text-slate-800 mb-3">We travel to patients across northern and western Johannesburg:</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {areas.map((area, index) => (
              <span key={index} className="px-3 py-1 bg-white rounded-full text-xs text-slate-700 font-medium shadow-sm">
                {area}
              </span>
            ))}
          </div>
          <p className="text-xs text-slate-800">
            If you are unsure whether your area is covered, please <Link to="/contact" className="underline font-medium">contact us</Link>.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-8 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Home className="w-8 h-8 text-[#5ce1e6] mx-auto mb-2" />
          <h2 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            To Book a Home Visit
          </h2>
          <p className="text-sm text-slate-300 mb-4">
            Call or WhatsApp to arrange.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a 
              href="tel:0659972538" 
              className="inline-flex items-center justify-center gap-2 px-6 py-2 bg-[#5ce1e6] text-slate-900 rounded-full text-sm font-medium hover:bg-[#22d3ee] transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call to Book
            </a>
            <a 
              href="https://wa.me/27659972538?text=Hi%20Bong,%20I'd%20like%20to%20book%20a%20home%20physiotherapy%20visit." 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-2 bg-green-500 text-white rounded-full text-sm font-medium hover:bg-green-600 transition-colors"
            >
              <Phone className="w-4 h-4" />
              WhatsApp
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
